import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Navigation from './components/Navigation';
import { AuthProvider } from './contexts/AuthContext';
import { ConferenceSettingsProvider } from './contexts/ConferenceSettingsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import Payment from './pages/Payment';
import SpeakerDashboard from './pages/SpeakerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ConferenceOverview from './components/ConferenceOverview';
import Hero from './components/Hero';
import HomeIncentives from './components/HomeIncentives';
import NewsHighlights from './components/NewsHighlights';
import Testimonials from './components/Testimonials';
import CallForPapers from './pages/CallForPapers';
import Sponsorship from './pages/Sponsorship';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import About from './pages/About';
import Schedule from './pages/Schedule';
import News from './pages/News';
import PreviousEdition from './components/PreviousEdition';
import VideoHighlight from './components/VideoHighlight';
import { fetchSpeakers, fetchSessions, fetchSponsors } from './services/api';
import { formatImageUrl } from './utils/imageUtils';
import './styles/index.css';
import Location from './pages/Location';
import Speakers from './pages/Speakers';
import Presentations from './pages/Presentations';
import Gallery from './pages/Gallery';
import Highlights from './pages/Highlights';
import Awards from './pages/Awards';
import FeaturedSpeakers from './components/FeaturedSpeakers';
import FeaturedSessions from './components/FeaturedSessions';
import SessionBadge from './components/SessionBadge';
import SessionBadgeGenerator from './pages/SessionBadgeGenerator';
import BadgeOnly from './components/BadgeOnly';
import SpeakerBanner from './components/SpeakerBanner';
import SpeakerBannerGenerator from './pages/SpeakerBannerGenerator';
import SpeakerBannerOnly from './components/SpeakerBannerOnly';

// Create a client
const queryClient = new QueryClient();

interface Speaker {
  id: string;
  name: string;
  title: string;
  image?: string;
  company?: string;
  bio_short?: string;
}

interface Session {
  id: string;
  title: string;
  description: string;
  type: string;
  speakers: { id: string; name: string; display_order: number }[];
  startTime: string;
  endTime: string;
  room: string;
  status: string;
}

interface Sponsor {
  id: string;
  name: string;
  logo_url?: string;
  description?: string;
  website?: string;
  tier: string;
}

const App: React.FC = () => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [speakersData, sessionsData, sponsorsData] = await Promise.all([
          fetchSpeakers(),
          fetchSessions(),
          fetchSponsors()
        ]);

        setSpeakers(speakersData.speakers);
        setSessions(sessionsData.sessions);
        setSponsors(sponsorsData.sponsors);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Transform sessions into calendar events
  const calendarEvents = sessions.map(session => ({
    ...session,
    start: new Date(session.startTime),
    end: new Date(session.endTime)
  }));

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ConferenceSettingsProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <Navigation />
              
              <Routes>
                <Route
                  path="/"
                  element={
                    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-16">
                      <Hero />
                      <HomeIncentives />
                      <ConferenceOverview />
                      
                      {/* Featured Speakers Section */}
                      <div className="container mx-auto px-4 py-16">
                        {loading ? (
                          <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
                          </div>
                        ) : (
                          <FeaturedSpeakers speakers={speakers} />
                        )}
                      </div>

                      {/* Featured Sessions */}
                      <main className="container mx-auto px-4 py-16">
                        {error && (
                          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8" role="alert">
                            {error}
                          </div>
                        )}

                        {loading ? (
                          <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
                          </div>
                        ) : (
                          <>
                            <FeaturedSessions sessions={sessions} />
                          </>
                        )}
                      </main>

                      {/* Sponsors and Partners moved up for higher prominence */}
                      <div className="container mx-auto px-4 py-8">
                        {loading ? (
                          <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
                          </div>
                        ) : (
                          <>
                            {sponsors.length > 0 && (
                              <section className="mb-12">
                                <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
                                  Our Sponsors
                                </h2>

                                {/* Platinum Sponsors */}
                                {sponsors.filter(s => s.tier === 'platinum').length > 0 && (
                                  <div className="mb-12">
                                    <h3 className="text-2xl font-semibold mb-4 text-gray-800">Platinum Sponsors</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                      {sponsors
                                        .filter(sponsor => sponsor.tier === 'platinum')
                                        .map((sponsor) => (
                                          <div 
                                            key={sponsor.id} 
                                            className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row items-center cursor-pointer" 
                                            onClick={() => sponsor.website && window.open(sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`, '_blank', 'noopener,noreferrer')}
                                          >
                                            <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6 w-40 h-40 flex items-center justify-center">
                                              <img 
                                                src={formatImageUrl(sponsor.logo_url, "/placeholder-sponsor.png")}
                                                alt={sponsor.name}
                                                className="max-h-32 max-w-full object-contain"
                                              />
                                            </div>
                                            <div className="flex-grow text-center md:text-left">
                                              <h4 className="text-xl font-bold text-gray-800">{sponsor.name}</h4>
                                              {sponsor.description && (
                                                <p className="text-gray-600 my-2">{sponsor.description}</p>
                                              )}
                                              {sponsor.website && (
                                                <a 
                                                  href={sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="inline-block mt-2 text-blue-600 hover:text-blue-800"
                                                >
                                                  Visit Website
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Gold Sponsors */}
                                {sponsors.filter(s => s.tier === 'gold').length > 0 && (
                                  <div className="mb-10">
                                    <h3 className="text-xl font-semibold mb-4 text-gray-800">Gold Sponsors</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                      {sponsors
                                        .filter(sponsor => sponsor.tier === 'gold')
                                        .map((sponsor) => (
                                          <div 
                                            key={sponsor.id} 
                                            className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer" 
                                            onClick={() => sponsor.website && window.open(sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`, '_blank', 'noopener,noreferrer')}
                                          >
                                            <div className="flex justify-center mb-4 h-24">
                                              <img 
                                                src={formatImageUrl(sponsor.logo_url, "/placeholder-sponsor.png")}
                                                alt={sponsor.name}
                                                className="max-h-20 max-w-full object-contain"
                                              />
                                            </div>
                                            <div className="text-center">
                                              <h4 className="text-lg font-bold text-gray-800">{sponsor.name}</h4>
                                              {sponsor.description && (
                                                <p className="text-gray-600 my-2 text-sm">{sponsor.description}</p>
                                              )}
                                              {sponsor.website && (
                                                <a 
                                                  href={sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="inline-block mt-2 text-blue-600 hover:text-blue-800 text-sm"
                                                >
                                                  Visit Website
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Silver Sponsors */}
                                {sponsors.filter(s => s.tier === 'silver').length > 0 && (
                                  <div className="mb-10">
                                    <h3 className="text-xl font-semibold mb-6 text-gray-800">Silver Sponsors</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                      {sponsors
                                        .filter(sponsor => sponsor.tier === 'silver')
                                        .map((sponsor) => (
                                          <div 
                                            key={sponsor.id} 
                                            className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer" 
                                            onClick={() => sponsor.website && window.open(sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`, '_blank', 'noopener,noreferrer')}
                                          >
                                            <div className="flex justify-center mb-4 h-24">
                                              <img 
                                                src={formatImageUrl(sponsor.logo_url, "/placeholder-sponsor.png")}
                                                alt={sponsor.name}
                                                className="max-h-20 max-w-full object-contain"
                                              />
                                            </div>
                                            <div className="text-center">
                                              <h4 className="text-lg font-bold text-gray-800">{sponsor.name}</h4>
                                              {sponsor.description && (
                                                <p className="text-gray-600 my-2 text-sm">{sponsor.description}</p>
                                              )}
                                              {sponsor.website && (
                                                <a 
                                                  href={sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="inline-block mt-2 text-blue-600 hover:text-blue-800 text-sm"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  Visit Website
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}

                                {/* Bronze Sponsors */}
                                {sponsors.filter(s => s.tier === 'bronze').length > 0 && (
                                  <div className="mb-10">
                                    <h3 className="text-xl font-semibold mb-6 text-gray-800">Bronze Sponsors</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                      {sponsors
                                        .filter(sponsor => sponsor.tier === 'bronze')
                                        .map((sponsor) => (
                                          <div 
                                            key={sponsor.id} 
                                            className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer" 
                                            onClick={() => sponsor.website && window.open(sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`, '_blank', 'noopener,noreferrer')}
                                          >
                                            <div className="flex justify-center mb-4 h-24">
                                              <img 
                                                src={formatImageUrl(sponsor.logo_url, "/placeholder-sponsor.png")}
                                                alt={sponsor.name}
                                                className="max-h-20 max-w-full object-contain"
                                              />
                                            </div>
                                            <div className="text-center">
                                              <h4 className="text-lg font-bold text-gray-800">{sponsor.name}</h4>
                                              {sponsor.description && (
                                                <p className="text-gray-600 my-2 text-sm">{sponsor.description}</p>
                                              )}
                                              {sponsor.website && (
                                                <a 
                                                  href={sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="inline-block mt-2 text-blue-600 hover:text-blue-800 text-sm"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  Visit Website
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </section>
                            )}

                            {/* Partners Section */}
                            {sponsors.filter(s => s.tier === 'partner').length > 0 && (
                              <section className="mb-16">
                                <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
                                  Our Partners
                                </h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                  {sponsors
                                    .filter(sponsor => sponsor.tier === 'partner')
                                    .map((sponsor) => (
                                      <div 
                                        key={sponsor.id} 
                                        className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer" 
                                        onClick={() => sponsor.website && window.open(sponsor.website.startsWith('http') ? sponsor.website : `https://${sponsor.website}`, '_blank', 'noopener,noreferrer')}
                                      >
                                        <div className="flex justify-center mb-4 h-24">
                                          <img 
                                            src={formatImageUrl(sponsor.logo_url, "/placeholder-sponsor.png")}
                                            alt={sponsor.name}
                                            className="max-h-20 max-w-full object-contain"
                                          />
                                        </div>
                                        <div className="text-center">
                                          <h4 className="text-lg font-bold text-gray-800">{sponsor.name}</h4>
                                          {sponsor.description && (
                                            <p className="text-gray-600 my-2 text-sm">{sponsor.description}</p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </section>
                            )}
                          </>
                        )}
                      </div>

                      <NewsHighlights />
                      <VideoHighlight />
                      <Testimonials />
                      <PreviousEdition />
                    </div>
                  }
                />
                
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['user']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/speaker"
                  element={
                    <ProtectedRoute allowedRoles={['speaker']}>
                      <SpeakerDashboard />
                    </ProtectedRoute>
                  }
                />
                
                <Route path="/payment" element={<Payment />} />
                <Route path="/cfp" element={<CallForPapers />} />
                <Route path="/sponsorship" element={<Sponsorship />} />
                <Route path="/about" element={<About />} />
                <Route path="/speakers" element={<Speakers />} />
                <Route path="/presentations" element={<Presentations />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/location" element={<Location />} />
                <Route path="/awards" element={<Awards />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/highlights" element={<Highlights />} />
                <Route path="/news" element={<News />} />
                <Route path="/badge-generator" element={<SessionBadgeGenerator />} />
                <Route path="/badge/:sessionId" element={<BadgeOnly />} />
                <Route path="/speaker-banner-generator" element={<SpeakerBannerGenerator />} />
                <Route path="/speaker-banner/:speakerId" element={<SpeakerBannerOnly />} />
                <Route path="/login" element={<Login />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
              </Routes>
            </div>
          </Router>
        </ConferenceSettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
